import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Grid, Switch, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { grey } from '@mui/material/colors';
import { makeStyles } from '@mui/styles';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Prompt, useHistory, useParams } from 'react-router';
import AccessDeniedView from '../components/AccessDeniedView';
import NavigateBackBar from '../components/NavigateBackBar';
import RecordDetails, { RecordDetailsContext } from '../components/RecordDetails';
import NotificationRule from './NotificationRule';
import RulesetLoading from './RulesetLoading';
import { computeRuleIssues, issuesForRule, serverRuleIssues, useRuleSchema } from './ruleValidation';

import { isEqual } from 'lodash';
import { DataAPIContext, GET_ONE } from '../api';
import { DELETE, UPDATE } from '../api/dataProvider';
import FormActionButtons from '../components/FormActionButtons';
import FormLicenseeContext, { toLicenseeId } from '../components/FormLicenseeContext';
import LicenseeFormField from '../components/LicenseeFormField';
import PageMeta from '../components/PageMeta';
import TabPanel, { a11yProps } from '../components/TabPanel';
import UsedByPanel from '../components/UsedByPanel';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import { RULES_MATCH_ALL, RULES_MATCH_ANY } from './utils';
import useConfirmDialog from '../components/useConfirmDialog';

const useStyles = makeStyles((theme) => ({
    root: {
        position: 'relative',
        overflowY: 'auto',
        [theme.breakpoints.down('sm')]: {
            height: `calc(${theme.cp.contentHeight} - 3.5rem)`,
        },
        margin: theme.spacing(2),
    },
    tabContent: {
        marginTop: theme.spacing(2),
    },
    backButtonNavbar: {
        display: 'flex',
        alignItems: 'center',
        padding: '0 1rem',
        marginLeft: '0',
    },
}));

const compareIdx = (a, b) => {
    if (a.idx < b.idx) {
        return -1;
    }
    if (a.idx > b.idx) {
        return 1;
    }
    return 0;
};

export const RulesetEditForm = (props) => {
    const [confirmDelete, confirmDeleteDialog] = useConfirmDialog();
    const history = useHistory();
    const dataAPI = React.useContext(DataAPIContext);
    const resource = 'notification_ruleset';
    const theme = useTheme();
    const classes = useStyles();
    const urlParams = useParams();
    const [ruleset, setRuleset] = React.useState({
        ...props.data,
        rules: (props.data.rules || []).map((r, idx) => ({ ...r, idx })),
    });
    const [currentTab, setCurrentTab] = React.useState(0);
    const [, setSyncActive] = React.useState(false);
    const isSmallScreen = !useIsWidthUp('sm');
    const { enqueueSnackbar } = useSnackbar();
    const { hasPerm } = React.useContext(ContextStore);
    const hasWritePerm = hasPerm('notification', 'write')

    const onLicenseeChange = (id) => {
        setRuleset((prev) => ({ ...prev, tour_operator: id, _changed: true }));
    };
    const page_title_length = isSmallScreen ? 40 : 120;

    const deepCloneRuleset = (ruleset) => {
        return JSON.parse(JSON.stringify({
            ...ruleset,
            rules: (ruleset.rules || []).map((r, idx) => ({ ...r, idx })),
        }));
    }

    const [initialRuleset, setInitialRuleset] = React.useState(deepCloneRuleset(props.data));

    const handleTabChange = (ev, newTab) => {
        setCurrentTab(newTab);
    };

    const tabPanelProps = (index, h) => {
        return {
            className: classes.tabPanel,
            value: currentTab,
            index: index,
        };
    };

    React.useEffect(() => {
        if (urlParams.id && (!ruleset || !ruleset.id)) {
            setSyncActive(true);
            dataAPI(GET_ONE, resource, { id: urlParams.id })
                .then((res) => {
                    console.log('get one', urlParams.id, res);
                    setRuleset({
                        ...res.data,
                        rules: (res.data.rules || []).map((r, idx) => ({ ...r, idx })),
                    });
                })
                .finally(() => setSyncActive(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlParams.id]);


    console.log('ruleset:xxx', ruleset);

    const onAddRule = () => {
        setRuleset((prev) => ({
            ...prev,
            rules: [
                ...prev.rules,
                {
                    type: 0,
                    tpl: 0,
                    args: null,
                    negate: false,
                    idx: prev.rules.length,
                    field: '',
                    op: 0,
                    value: '',
                    ruleset: null,
                },
            ],
        }));
    };

    const onUpdateRule = (rule) => {
        console.log(`[onUpdateRule] Rule index: ${rule.idx}`);
        setRuleset((prev) => {
            const newRules = prev.rules.map((r) => {
                if (r.idx === rule.idx) {
                    return rule;
                }
                return r;
            });
            return { ...prev, rules: newRules };
        });
    };

    const onRemoveRule = async (idx) => {
        if (await confirmDelete({ body: 'Are you sure you want to delete this template from the ruleset?' })) {
            setRuleset((prev) => {
                console.log('onRemoveRule: prev', prev, idx);
                let new_idx_rules = prev.rules.filter((ur, urIdx) => urIdx !== idx);
                if (new_idx_rules.length > 0) {
                    for (let index = 0; index < new_idx_rules.length; index++) {
                        new_idx_rules[index].idx = index;
                    }
                }
                console.log('onRemoveRule: new_idx_rules', new_idx_rules);
                return { ...prev, rules: [...new_idx_rules] };
            });
        }
    };

    const onTextChange = (ev) => {
        setRuleset((prev) => ({
            ...prev,
            [ev.target.name]: ev.target.value,
        }));
    }

    const hasChanges = !isEqual(deepCloneRuleset(ruleset), initialRuleset);

    // Rule validation against the backend's rule-args schema: issues on new or
    // changed rules block the save (the server would answer 406), issues on rules
    // saved unchanged only warn. Server 406 row errors are shown until the rules
    // change (they are positional).
    const ruleSchema = useRuleSchema();
    const ruleIssues = React.useMemo(
        () => computeRuleIssues(ruleSchema, ruleset.rules, initialRuleset.rules),
        [ruleSchema, ruleset.rules, initialRuleset],
    );
    const [serverIssues, setServerIssues] = React.useState({ byIdx: {}, rulesKey: null });
    const rulesKey = JSON.stringify(ruleset.rules || []);
    const shownServerIssues = serverIssues.rulesKey === rulesKey ? serverIssues.byIdx : {};
    // Set once the ruleset is deleted so the redirect to the list doesn't trip the
    // leave prompt over edits that no longer have anything to be saved to.
    const skipLeavePrompt = React.useRef(false);
    const leavePromptMessage = () => skipLeavePrompt.current || 'Are you sure you want to leave without saving changes?';

    const onSave = (ev) => {
        if (ruleIssues.blocking) {
            enqueueSnackbar('Fix the rules marked in red before saving.', { variant: 'error' });
            return;
        }
        const sentRules = ruleset.rules || [];
        setSyncActive(true);
        dataAPI(UPDATE, resource, { id: ruleset.id, data: ruleset })
            .then(res => {
                if (res.statusCode === 200) {
                    enqueueSnackbar("Changes saved", { variant: 'success' });
                    // rule_warnings (legacy rules saved unchanged) are recomputed client-side.
                    const { rule_warnings: _ruleWarnings, ...data } = res.data || {};
                    const savedRuleset = {
                        ...data,
                        rules: (data.rules || []).map((r, idx) => ({ ...r, idx })),
                    };
                    setRuleset(savedRuleset);
                    setInitialRuleset(deepCloneRuleset(savedRuleset));
                    setServerIssues({ byIdx: {}, rulesKey: null });
                } else if (res.statusCode === 406 && res.data && Array.isArray(res.data.errors)) {
                    setServerIssues({ byIdx: serverRuleIssues(res.data.errors, sentRules), rulesKey: JSON.stringify(sentRules) });
                    enqueueSnackbar(res.data.message || 'Some rules are invalid.', { variant: 'error' });
                } else {
                    enqueueSnackbar(`Error saving changes (${res.statusCode})`, { variant: 'error' });
                }
            })
            .catch(err => {
                console.error('Error saving changes', err);
                enqueueSnackbar(`It looks like our servers are on maintenance. Please try again in a few minutes`, { variant: 'error' });
            })
            .finally(() => {
                setSyncActive(false);
            });
    }

    const onDelete = async (ev) => {
        if (await confirmDelete({ body: 'Are you sure you want to delete this ruleset?' })) {
            setSyncActive(true);
            // Clear syncActive before navigating: the push unmounts this form.
            dataAPI(DELETE, resource, { id: ruleset.id })
                .then((res) => {
                    setSyncActive(false);
                    if (res.statusCode === 204) {
                        enqueueSnackbar('Ruleset deleted successfully', { variant: 'success' });
                        skipLeavePrompt.current = true;
                        history.push('/notifications/rulesets');
                    } else {
                        // A ruleset still in use is refused with where it is used (406 ruleset_in_use, CP-7771).
                        enqueueSnackbar(res.data?.message || `Ruleset deletion failed (${res.statusCode})`, { variant: 'error', autoHideDuration: 8000 });
                    }
                })
                .catch((err) => {
                    setSyncActive(false);
                    enqueueSnackbar(`Ruleset deletion failed (${err})`, { variant: 'error' });
                });
        }
    }

    // CP-8423: back to the rulesets list, same target as the NavigateBackBar
    // arrow; the <Prompt> still guards unsaved changes.
    const onCancel = () => {
        history.push('/notifications/rulesets');
    };

    return (<FormLicenseeContext.Provider value={toLicenseeId(ruleset.tour_operator)}>
    <div className={classes.root}>
        {confirmDeleteDialog}
        <PageMeta title={`Edit Ruleset${ruleset && ruleset.name ? ' - ' + ruleset.name : ''}`} />
        <Prompt when={hasChanges && hasWritePerm} message={leavePromptMessage} />
        <Grid container direction="row" spacing={2} sx={{ position: 'relative' }}>
            <Grid item xs={12} container>
                <NavigateBackBar
                    label={('Ruleset' + (ruleset && ruleset.name ? ' - ' + ruleset.name : ''))}
                    maxLabelLength={page_title_length}
                    url={'/notifications/rulesets'}
                    backButtonNavbarStyle={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 !important',
                        paddingTop: '0 !important',
                    }}
                    labelStyle={{
                        fontSize: '1.25rem',
                        fontWeight: 500,
                        lineHeight: 1.6,
                        marginLeft: 0
                    }}
                />
            </Grid>
            <Grid item xs={12}>
                <Tabs value={currentTab} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
                    <Tab label="General" {...a11yProps(0)} />
                    <Tab label="Used in" {...a11yProps(1)} />
                </Tabs>
                <TabPanel {...tabPanelProps(0)} className={classes.tabContent}>
                    <Grid container direction="column" wrap="nowrap" spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <LicenseeFormField value={ruleset.tour_operator} onChange={onLicenseeChange} disabled={!hasWritePerm} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="name"
                                label="Ruleset name"
                                variant="filled"
                                fullWidth
                                value={ruleset.name}
                                onChange={onTextChange}
                                error={!ruleset.name}
                                helperText={!ruleset.name ? "Ruleset name is required" : ""}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                name="codename"
                                label="Codename"
                                variant="filled"
                                fullWidth
                                value={ruleset.codename || ''}
                                onChange={onTextChange}
                                error={!!ruleset.codename && !/^[A-Za-z][A-Za-z0-9_-]*$/.test(ruleset.codename)}
                                helperText={
                                    !!ruleset.codename && !/^[A-Za-z][A-Za-z0-9_-]*$/.test(ruleset.codename)
                                        ? "Must start with a letter; only letters, digits, hyphens and underscores allowed"
                                        : "Optional alias, unique per operator. Use it in {% if-ruleset %} notification template tags."
                                }
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}></Grid>

                        {ruleset.rules && ruleset.rules.length && (<>
                            <Grid item xs={12} container direction="row" spacing={2} sx={{ marginTop: theme.spacing(2), marginLeft: theme.spacing(2), marginRight: theme.spacing(2), border: 1, borderRadius: 10, borderColor: grey[300], maxWidth: 'lg' }}>
                                <Grid item xs={12} container direction="row" spacing={2}>
                                    {ruleset.rules?.sort(compareIdx).map((rule, ruleIdx) => {
                                        return (
                                            <Grid item xs={12} container alignItems="center" p={2} key={ruleIdx}>
                                                <NotificationRule
                                                    disabled={!hasWritePerm}
                                                    data={rule}
                                                    issues={issuesForRule(rule.idx, ruleIssues.byIdx, shownServerIssues)}
                                                    disableDeleteButton={ruleset.rules.filter((ur) => ur.created).length > 0 && !rule.created}
                                                    key={rule.idx}
                                                    setNotification={setRuleset}
                                                    onUpdateRule={onUpdateRule}
                                                    handleRemoveRule={() => onRemoveRule(ruleIdx)}
                                                    isSmallScreen={isSmallScreen}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                                {ruleset.rules && ruleset.rules.length >= 0 ? (
                                    <Grid item xs={12} container alignItems="center" justifyContent={!isSmallScreen && 'flex-start'} p={!isSmallScreen && 2} style={{ paddingTop: 0 }}>
                                        <Typography style={{ marginLeft: '1rem', fontWeight: (ruleset.match_type === RULES_MATCH_ALL || !ruleset.match_type) && 700 }}>Match All</Typography>
                                        <Switch
                                            disabled={!hasWritePerm}
                                            name="match_type"
                                            color="primary"
                                            checked={ruleset.match_type === RULES_MATCH_ANY}
                                            onChange={() => {
                                                setRuleset((prev) => ({
                                                    ...prev,
                                                    match_type: prev.match_type === RULES_MATCH_ALL || !prev.match_type ? RULES_MATCH_ANY : RULES_MATCH_ALL,
                                                }));
                                            }}
                                        />
                                        <Typography style={{ fontWeight: ruleset.match_type === RULES_MATCH_ANY && 700 }}>Match Any</Typography>
                                    </Grid>
                                ) : (
                                    ''
                                )}
                            </Grid>
                        </>)}
                    </Grid>
                    <Grid item xs={12} container justifyContent="flex-start" mt={2}>
                        <Button disabled={!hasWritePerm} onClick={onAddRule} variant="outlined" color="primary">
                            <AddIcon fontSize="small"/>&nbsp;Add rule
                        </Button>
                    </Grid>
                    {/* No maxWidth cap: on wide screens it left the buttons mid-page instead of at the right edge. */}
                    <Box m={2} p={2}>
                        <FormActionButtons
                            mode="edit"
                            size="medium"
                            onSubmit={onSave}
                            submitDisabled={!ruleset.name || !hasChanges}
                            onCancel={onCancel}
                            onDelete={onDelete}
                            deletePerm="notification.delete"
                        />
                    </Box>
                </TabPanel>
                <TabPanel {...tabPanelProps(1)} className={classes.tabContent}>
                    <UsedByPanel resource={resource} id={urlParams.id} />
                </TabPanel>
            </Grid>
        </Grid>
    </div>
    </FormLicenseeContext.Provider>);
}

const RulesetEdit = (props) => {
    const resource = 'notification_ruleset';
    const routeParams = useParams();
    const id = routeParams.id;
    const { hasPerm } = React.useContext(ContextStore);


    if (!hasPerm('notification', 'read')) {
        return <AccessDeniedView />
    }
    return (
        <RecordDetails resource={resource} id={id} fullResponse>
            <RecordDetailsContext.Consumer>{(res) => (res.statusCode === 200 ? <RulesetEditForm {...props} resource={resource} id={id} data={res.data} /> : <RulesetLoading {...props} resource={resource} id={id} res={res} />)}</RecordDetailsContext.Consumer>
        </RecordDetails>
    );
};

export default RulesetEdit;
